import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import companySidebar from '../../../../assets/images/icons/companySidebar.svg';
import home from '../../../../assets/images/icons/home.svg';
import user from '../../../../assets/images/icons/user.svg';
import useThemeContext from '../../../../contexts/theme';
import useErrors from '../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../hooks/useQuery';
import formatPhone from '../../../../utils/formatPhone';
import isEmailValid from '../../../../utils/isEmailValid';
import onlyNumbers from '../../../../utils/onlyNumbers';
import { CustomStyle } from '../../../CustomSelectStyle';
import { CustomStyle as CustomStyleDarkTheme } from '../../../CustomSelectStyleDarkTheme';
import FormGroup from '../../../FormGroup';
import FullAddressForm from '../../../FullAddressForm';
import Input from '../../../Input';
import MyModal from '../../../Modal';
import { SecondaryButton } from '../../../SecondaryButton';
import { DadosConsulta, EnderecosLt, IWorkplacesOptions, LinhasBia, OpBia } from '../../types';
import { InnerDataContainer, SelectedViewContainer } from './styles';

interface IEditDataModal {
  editConsultDataModalShow: boolean,
  setEditConsultDataModalShow: Dispatch<SetStateAction<boolean>>;
  consultData: DadosConsulta,
  setConsultData: Dispatch<SetStateAction<DadosConsulta>>,
  newConsultData: DadosConsulta,
  setNewConsultData: Dispatch<SetStateAction<DadosConsulta>>,
  setIsSomethingLoading: Dispatch<SetStateAction<boolean>>,
  workplaces: EnderecosLt[];
  companyLabel: string;
  canEnableActions: boolean;
  isMultiRoute: boolean;
  selectedWorkplaces: IWorkplacesOptions[];
  setSelectedWorkplaces: Dispatch<SetStateAction<IWorkplacesOptions[]>>
  routeName: string;
  setRouteName: Dispatch<SetStateAction<string>>;
  setOpsBias: Dispatch<SetStateAction<OpBia[]>>;
  setLinesBias: Dispatch<SetStateAction<LinhasBia[]>>;
}

export default function EditDataModal({
  editConsultDataModalShow,
  setEditConsultDataModalShow,
  setIsSomethingLoading,
  consultData,
  setConsultData,
  newConsultData,
  setNewConsultData,
  workplaces,
  companyLabel,
  canEnableActions,
  isMultiRoute,
  selectedWorkplaces,
  setSelectedWorkplaces,
  routeName,
  setRouteName,
  setOpsBias,
  setLinesBias,
}: IEditDataModal) {
  const correspondingWorkplace = useMemo(() => (workplaces?.find((wp) => wp?.codEndA === newConsultData.codEndA)), [newConsultData.codEndA, workplaces]);
  const valueLabelByCorrespondingWorkplace: IWorkplacesOptions = useMemo(() => (
    { value: correspondingWorkplace?.codEndA ?? 0, label: `${correspondingWorkplace?.denominacaoLT} ${correspondingWorkplace?.distanceFromEmployeeHome ? `(${(correspondingWorkplace?.distanceFromEmployeeHome / 1000).toFixed(2)}km)` : ''}`, ...correspondingWorkplace } as IWorkplacesOptions
  ), [correspondingWorkplace]);

  const workplacesOptions: IWorkplacesOptions[] = useMemo(() => {
    const { uf } = newConsultData;

    const exceptions = [['GO', 'DF'], ['GO', 'TO'], ['MA', 'PI'], ['PE', 'BA'], ['SP', 'MG'], ['MG', 'RJ'], ['SP', 'RJ'], ['PR', 'SC']];
    const exceptionGroupsOfUf = exceptions.filter((exception) => exception.includes(uf));

    const filteredWorkplaces = workplaces.filter((wp) => {
      if (!uf) return true;

      if (!exceptionGroupsOfUf || exceptionGroupsOfUf.length === 0) {
        return wp.UF === uf;
      }

      return exceptionGroupsOfUf.some((exception) => exception.includes(wp.UF));
    });

    const mappedWorkplaces = filteredWorkplaces?.map((wp) => ({ value: wp?.codEndA, label: `${wp?.denominacaoLT} ${wp?.distanceFromEmployeeHome ? `(${(wp?.distanceFromEmployeeHome / 1000).toFixed(2)}km)` : ''}`, ...wp }));

    return mappedWorkplaces || [];
  }, [newConsultData, workplaces]);

  useEffect(() => {
    if (workplacesOptions.length === 0) {
      setNewConsultData((prevState) => ({
        ...prevState,
        codEndA: 0,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workplacesOptions]);


  const query = useQuery();
  const companyCode = query.get('codEmpresa');
  const navigate = useNavigate();
  const { isCustomer } = useGetGeneralAppData();

  const {
    setError, removeError, getErrorMessageByFieldName, errors
  } = useErrors();
  const { selectedTheme } = useThemeContext();
  const { handleSelectedCompanyChange } = useGetGeneralAppData();

  const hasNewCoordsSettled = useMemo(() => (
    (newConsultData.lat && newConsultData.lng) || (newConsultData.lat !== consultData.lat && newConsultData.lng !== consultData.lng)
  ), [consultData.lat, consultData.lng, newConsultData.lat, newConsultData.lng]);

  const newCoords = useMemo(() => (
    hasNewCoordsSettled ? (`${newConsultData.lat}, ${newConsultData.lng}`) : ''
  ), [hasNewCoordsSettled, newConsultData.lat, newConsultData.lng]);

  const hasAlreadyCoordsSettled = useMemo(() => (
    consultData.lat && consultData.lng
  ), [consultData.lat, consultData.lng]);

  const currentCoords = useMemo(() => (
    hasAlreadyCoordsSettled ? (`${consultData.lat}, ${consultData.lng}`) : ''
  ), [hasAlreadyCoordsSettled, consultData.lat, consultData.lng]);

  const animatedComponents = makeAnimated();

  function handleRegistrationChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      matricula: event.target.value
    }));
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      nome: event.target.value
    }));

    if (!event.target.value) {
      setError({ field: 'name', message: 'Nome é obrigatório!' });
    } else {
      removeError('name');
    }
  }

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      mail: event.target.value
    }));

    if (!event.target.value) {
      setError({ field: 'email', message: 'E-mail é obrigatório!' });
    } else if (!isEmailValid(event.target.value)) {
      setError({ field: 'email', message: 'E-mail inválido!' });
    } else {
      removeError('email');
    }
  }

  function handleCellphoneChange(event: ChangeEvent<HTMLInputElement>) {
    setNewConsultData((prevState) => ({
      ...prevState,
      celular: formatPhone(event.target.value),
    }));

    if (!event.target.value) {
      removeError('cellphone');
    } else if (formatPhone(event.target.value).length < 14) {
      setError({ field: 'cellphone', message: 'Digite um celular válido!' });
    } else {
      removeError('cellphone');
    }
  }

  function onLatChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      lat: value ? Number(onlyNumbers(value).toFixed(6)) : '',
    }));
  }

  function onLngChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      lng: value ? Number(onlyNumbers(value).toFixed(6)) : '',
    }));
  }

  function onCepChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      cep: value,
    }));
  }

  function onStreetNameChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      logradouro: value,
    }));
  }

  function onNumberChange(value: string | number) {
    setNewConsultData((prevState) => ({
      ...prevState,
      num: Number(value) || ''
    }));
  }

  function onDistrictChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      bairro: value
    }));
  }

  function onCityChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      cidade: value
    }));
  }

  function onUfChange(value: string) {
    setNewConsultData((prevState) => ({
      ...prevState,
      uf: value
    }));
  }

  function handleSelectedWorkplaceChange(wp: IWorkplacesOptions) {
    setNewConsultData((prevState) => ({
      ...prevState,
      codEndA: Number(wp.value),
      semIntegraVT: wp.semIntegrarRJ,
      semTaxaVT: wp.semTaxaSPtrans,
      valorTeto: wp.valTetoEnd,
      usaTeto: !!(wp.valTetoEnd),
      distancia: wp.distanciaEnd || prevState.distancia,
      usaFretado: wp.temFretamento,
      opcaoVTFreta: wp.opcaoVTFreta,
      temLimitadorKM: wp.temLimitadorKM,
      KM: wp.KM,
    }));

    setOpsBias([]);
    setLinesBias([]);
  }

  function handleRouteNameChange(event: ChangeEvent<HTMLInputElement>) {
    setRouteName(event.target.value);

    if (!event.target.value) {
      setError({ field: 'routeName', message: 'Denominação da rota é obrigatório!' });
    } else {
      removeError('routeName');
    }
  }

  const location = useLocation();

  const navigateToWorkplaceEditPage = (workplaceCode?: number) => {
    handleSelectedCompanyChange({ value: Number(companyCode)!, label: companyLabel });
    const currentPath = `${location.pathname}${location.search}`;
    navigate(`/workplaces/${workplaceCode || valueLabelByCorrespondingWorkplace.codEnd}?returnLink=${encodeURIComponent(currentPath)}`);
  };

  function confirmChanges() {
    setEditConsultDataModalShow(false);
    setConsultData(newConsultData);
  }

  const isValidToConfirm = useMemo(() => {
    const { cpf, mail, nome, cep, logradouro, num, bairro, cidade, uf, lat, lng } = newConsultData;
    console.log({ cpf, mail, nome, cep, logradouro, num, bairro, cidade, uf, lat, lng, errors });

    return (
      errors.length === 0 && cpf && mail && nome && cep && logradouro && num && bairro && cidade && uf && lat && lng && (!isMultiRoute || !!routeName)
    );
  }, [errors, isMultiRoute, newConsultData, routeName]);

  useEffect(() => {
    setNewConsultData(consultData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MyModal
      show={editConsultDataModalShow}
      title="Editar dados da consulta"
      modalBody={(
        <SelectedViewContainer>
          <InnerDataContainer>
            <h4>
              <img src={user} alt="" />
              Dados funcionário
            </h4>
            <FormGroup error={getErrorMessageByFieldName('registration')}>
              <label htmlFor="contactName">Matrícula</label>
              <Input
                placeholder="Matrícula do colaborador"
                value={newConsultData.matricula ?? consultData.matricula}
                onChange={handleRegistrationChange}
                autoComplete="new-password"
                darkBorder
                error={getErrorMessageByFieldName('registration')}
              />
            </FormGroup>

            <FormGroup error={getErrorMessageByFieldName('name')}>
              <label htmlFor="contactName">Nome</label>
              <Input
                placeholder="Nome do colaborador"
                value={newConsultData.nome ?? consultData.nome}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onChange={isCustomer ? () => {} : handleNameChange}
                autoComplete="new-password"
                darkBorder
                error={getErrorMessageByFieldName('name')}
                disabled={isCustomer}
              />
            </FormGroup>

            <FormGroup error={getErrorMessageByFieldName('email')}>
              <label htmlFor="contactName">E-mail</label>
              <Input
                placeholder="E-mail do colaborador"
                value={newConsultData.mail ?? consultData.mail}
                onChange={handleEmailChange}
                autoComplete="new-password"
                darkBorder
                error={getErrorMessageByFieldName('email')}
              />
            </FormGroup>

            <FormGroup error={getErrorMessageByFieldName('cellphone')}>
              <label htmlFor="contactName">Celular</label>
              <Input
                placeholder="Celular do colaborador"
                value={newConsultData.celular ?? consultData.celular}
                onChange={handleCellphoneChange}
                autoComplete="new-password"
                darkBorder
                error={getErrorMessageByFieldName('cellphone')}
              />
            </FormGroup>
          </InnerDataContainer>

          <InnerDataContainer>
            <h4>
              <img src={home} alt="" />
              Endereço funcionário
            </h4>

            <FullAddressForm
              lat={(hasNewCoordsSettled ? newCoords.split(', ')[0] : currentCoords.split(', ')[0]) ?? ''}
              lng={(hasNewCoordsSettled ? newCoords.split(', ')[1] : currentCoords.split(', ')[1]) ?? ''}
              cep={newConsultData.cep ?? consultData.cep}
              streetName={newConsultData.logradouro ?? consultData.logradouro}
              number={newConsultData.num ?? consultData.num}
              district={newConsultData.bairro ?? consultData.bairro}
              city={newConsultData.cidade ?? consultData.cidade}
              uf={newConsultData.uf ?? consultData.uf}
              onLatChange={onLatChange}
              onLngChange={onLngChange}
              onCepChange={onCepChange}
              onStreetNameChange={onStreetNameChange}
              onNumberChange={onNumberChange}
              onDistrictChange={onDistrictChange}
              onCityChange={onCityChange}
              onUfChange={onUfChange}
              setError={setError}
              setIsLoading={setIsSomethingLoading}
              removeError={removeError}
              getErrorMessageByFieldName={getErrorMessageByFieldName}
            />
          </InnerDataContainer>

          <InnerDataContainer>
            <h4>
              <img src={companySidebar} alt="" />
              Local de trabalho
            </h4>
            {isMultiRoute && (
              <FormGroup error={getErrorMessageByFieldName('routeName')}>
                <label htmlFor="contactName">Denominação da Rota</label>
                <Input
                  placeholder="Ex.: Segunda, Terça, e Sexta-feira"
                  value={routeName}
                  onChange={handleRouteNameChange}
                  autoComplete="new-password"
                  darkBorder
                  error={getErrorMessageByFieldName('routeName')}
                />
              </FormGroup>
            )}

            <FormGroup>
              <label htmlFor="contactName">
                {!isMultiRoute ? 'Selecione o local de trabalho' : 'Selecione os locais de trabalho'}
              </label>
              <Select
                value={!isMultiRoute ? valueLabelByCorrespondingWorkplace : selectedWorkplaces}
                options={workplacesOptions}
                onChange={!isMultiRoute ? (wp) => handleSelectedWorkplaceChange(wp as IWorkplacesOptions) : (opt) => {
                  setSelectedWorkplaces(opt as IWorkplacesOptions[]);
                }}
                placeholder={!isMultiRoute ? 'Selecione o local de trabalho' : 'Selecione os locais de trabalho'}
                styles={selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle}
                classNamePrefix="react-select"
                className="react-select-container"
                isMulti={isMultiRoute}
                components={animatedComponents}
              />
            </FormGroup>

            {(!!correspondingWorkplace || selectedWorkplaces.length > 0) && (
              <>
                {!isMultiRoute && (
                  <>
                    <small>
                      {correspondingWorkplace?.logradouro}, {correspondingWorkplace?.numero} <br /> {correspondingWorkplace?.bairro} - {correspondingWorkplace?.cidade} - {correspondingWorkplace?.UF} <br /> Coordenadas: {correspondingWorkplace?.lat ?? 0}, {correspondingWorkplace?.lng ?? 0}
                    </small>

                    {!isCustomer && (
                      <SecondaryButton style={{ marginTop: 8 }} onClick={() => navigateToWorkplaceEditPage()}>
                                  Corrigir Endereço Selecionado
                      </SecondaryButton>
                    )}
                  </>
                )}

                {isMultiRoute && (
                  <div className='workplacesContainer'>
                    {selectedWorkplaces.map((workplace, index) => (
                      <div className='workplaceInfoContainer' key={index}>
                        <small>
                                    #{index + 1} {workplace?.logradouro}, {workplace?.numero} <br /> {workplace?.bairro} - {workplace?.cidade} - {workplace?.UF} <br /> Coordenadas: {workplace?.lat ?? 0}, {workplace?.lng ?? 0}
                        </small>

                        {!isCustomer && (
                          <SecondaryButton small style={{ marginTop: 8 }} onClick={() => navigateToWorkplaceEditPage(workplace?.codEnd)}>
                                      Corrigir
                          </SecondaryButton>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </InnerDataContainer>
        </SelectedViewContainer>
      )}
      closeButtonLabel="Fechar"
      actionButtonLabel="Confirmar"
      onClose={() => {
        setEditConsultDataModalShow(false);
        setNewConsultData(consultData);
      }}
      onAction={() => confirmChanges()}
      isActionButtonDisabled={!isValidToConfirm || !canEnableActions}
      type="action"
      size='xl'
    />
  );
}
